/**
 * React integration.
 *
 * React swallows render errors into an error boundary, so without this an
 * entire class of failures never reaches a global handler.
 *
 * Marked as a client module so React Server Component trees (Next.js) can
 * import it: an error boundary is a class component and only runs client-side.
 */

'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';

// With the extension, because the published build emits this import verbatim
// and it must name the built sibling file, sharing its client rather than
// bundling a second copy of the SDK state.
import { captureException } from './index.js';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the children once an error has been caught. */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** Called after the error is reported. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    void captureException(error);
    this.props.onError?.(error, info);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(error, this.reset);
    return fallback ?? null;
  }
}
